import type { ReactNode } from "react";
import { TicketDeliveredFilesNoteCard } from "@/modules/tickets/TicketDeliveredFilesNoteCard";
import { TicketInvoiceSentNoteCard } from "@/modules/tickets/TicketInvoiceSentNoteCard";
import { TicketStatusChangeNoteCard } from "@/modules/tickets/TicketStatusChangeNoteCard";
import {
  InternalNotePlainBody,
  stripInternalNoteMarkdown,
} from "@/modules/tickets/InternalNotePlainBody";
import {
  isDeliveredFilesInternalNote,
  previewDeliveredFilesInternalNote,
} from "@/modules/tickets/parseDeliveredFilesInternalNote";
import {
  isInvoiceSentInternalNote,
  previewInvoiceSentInternalNote,
} from "@/modules/tickets/parseInvoiceSentInternalNote";
import {
  isStatusChangeInternalNote,
  previewStatusChangeInternalNote,
} from "@/modules/tickets/parseStatusChangeInternalNote";
import { ticketMessagePreviewText } from "@/modules/tickets/ticketMessagePreviewText";
import type { TicketMessage } from "@/modules/types";

/** Collapsed one-line preview for internal notes (and fallbacks). */
export const ticketInternalNotePreview = (
  message: TicketMessage,
  maxLength = 140,
) => {
  const invoicePreview = previewInvoiceSentInternalNote(message.body);
  if (invoicePreview) return invoicePreview;
  const deliveredPreview = previewDeliveredFilesInternalNote(message.body);
  if (deliveredPreview) return deliveredPreview;
  const statusPreview = previewStatusChangeInternalNote(message.body);
  if (statusPreview) return statusPreview;

  const preview = ticketMessagePreviewText(message, maxLength);
  if (!preview) return "No message content";
  return stripInternalNoteMarkdown(preview);
};

/**
 * Structured cards for billing/status notes; light markdown for other plain notes.
 */
export const TicketInternalNoteBody = ({
  body,
  htmlBody,
  fallback,
}: {
  body?: string | null;
  htmlBody?: string | null;
  /** Used when the note has HTML (or is not a known structured plain note). */
  fallback?: ReactNode;
}) => {
  if (isInvoiceSentInternalNote(body)) {
    return <TicketInvoiceSentNoteCard body={body} />;
  }
  if (isDeliveredFilesInternalNote(body)) {
    return <TicketDeliveredFilesNoteCard body={body} />;
  }
  if (isStatusChangeInternalNote(body)) {
    return <TicketStatusChangeNoteCard body={body} />;
  }
  if (body?.trim() && !htmlBody?.trim()) {
    return (
      <div className="mt-1">
        <InternalNotePlainBody text={body} />
      </div>
    );
  }
  return <>{fallback ?? null}</>;
};
